import { Inject } from '@wendellhu/redi';
import { SolutionService } from './solution.service';
import type {
  ActiveSolutionCardsQueryParams,
  CaseMetaDto,
  HomeCompletionStatsDto,
  HomeCompletionStatsQueryParams,
  SolutionAppendsQueryParams,
  SolutionHomeCardDto,
  SolutionImagingParseStateDto,
  SolutionListItemDto,
  SolutionPlanSetupDto,
} from './dto/solution.dto';
import type { SolutionCreateParams } from './dto/solution-create.dto';
import type { EngrEngineeringDTO } from '@holoview/dao-gen/generated';

export class SolutionController {
  constructor(
    @Inject(SolutionService) private readonly service: SolutionService,
  ) {}

  async getCaseMeta(solutionId: number): Promise<CaseMetaDto> {
    if (!solutionId || solutionId <= 0) throw new Error('Invalid solution id');
    return this.service.getCaseMeta(solutionId);
  }

  async getVersions(solutionId: number) {
    return this.service.getVersions(solutionId);
  }

  async findMdpsProductionOrderIds(solutionIds: number[]): Promise<Map<number, number>> {
    return this.service.findMdpsProductionOrderIds(solutionIds);
  }

  async setImagingSelection(solutionId: number, imagingId: number | null, activeSeriesId: string): Promise<void> {
    await this.service.setImagingSelection(solutionId, imagingId, activeSeriesId);
  }

  async updateStatus(params: {
    solutionId: number;
    caseId: number;
    versionId: number;
    status: string;
  }): Promise<void> {
    await this.service.updateStatus(params);
  }

  async createVersion(solutionId: number): Promise<{ id: number }> {
    return this.service.createVersion(solutionId);
  }

  async createSolution(params: SolutionCreateParams): Promise<EngrEngineeringDTO> {
    if (!params || !params.name) throw new Error('Solution name is required');
    return this.service.createSolution(params);
  }

  async findSolutionsByCase(caseId: number): Promise<SolutionListItemDto[]> {
    if (!caseId || caseId <= 0) throw new Error('Invalid case id');
    return this.service.findSolutionsByCase(caseId);
  }

  async findLatestSolutionPlanSetupByCase(caseId: number): Promise<SolutionPlanSetupDto | null> {
    if (!caseId || caseId <= 0) throw new Error('Invalid case id');
    return this.service.findLatestSolutionPlanSetupByCase(caseId);
  }

  async updateCasePlanSetup(caseId: number, planSetup: Pick<SolutionPlanSetupDto, 'planSetupAi' | 'planSetupHolo' | 'planSetupMdps'>): Promise<void> {
    if (!caseId || caseId <= 0) throw new Error('Invalid case id');
    await this.service.updateCasePlanSetup(caseId, planSetup);
  }

  async fetchSolutionsWithAppends(params: SolutionAppendsQueryParams): Promise<Record<string, unknown>[]> {
    return this.service.fetchSolutionsWithAppends(params);
  }

  async getActiveSolutionCards(params: ActiveSolutionCardsQueryParams): Promise<SolutionHomeCardDto[]> {
    return this.service.getActiveSolutionCards(params);
  }

  async getHomeCompletionStats(params: HomeCompletionStatsQueryParams): Promise<HomeCompletionStatsDto> {
    if (!params.currentUserId || params.currentUserId <= 0) {
      return {
        todayCompleted: 0,
        monthCompleted: 0,
        careerCompleted: 0,
        excellentReviews: 0,
      };
    }
    return this.service.getHomeCompletionStats(params);
  }

  async getImagingParseState(solutionId: number): Promise<SolutionImagingParseStateDto | null> {
    if (!solutionId || solutionId <= 0) throw new Error('Invalid solution id');
    return this.service.getImagingParseState(solutionId);
  }
}
